import { defineStore } from 'pinia'
import { ref, watch } from 'vue'
import type { RouteLocationNormalizedLoaded } from 'vue-router'

/**
 * 标签页项
 */
export interface TabItem {
  /** 路由路径 */
  path: string
  /** 路由名称 */
  name: string
  /** 显示标题 */
  title: string
  /** 图标 */
  icon?: string
  /** 是否可关闭 */
  closable: boolean
  /** 完整路由信息 */
  fullPath: string
  /** 查询参数 */
  query?: Record<string, any>
}

/**
 * 标签页状态管理
 */
export const useTabsStore = defineStore('tabs', () => {
  // 已打开的标签列表
  const tabs = ref<TabItem[]>([])

  // 当前激活的标签路径
  const activeTabPath = ref<string>('')

  /**
   * 添加标签页
   */
  const addTab = (route: RouteLocationNormalizedLoaded) => {
    // 跳过登录页、404等特殊页面
    if (!route.name || route.meta?.noTab) {
      return
    }

    const path = route.path
    const existingTab = tabs.value.find((tab) => tab.path === path)

    // 如果标签已存在，只更新 fullPath 和 query
    if (existingTab) {
      existingTab.fullPath = route.fullPath
      existingTab.query = route.query
      activeTabPath.value = path
      return
    }

    // 创建新标签
    const newTab: TabItem = {
      path,
      name: route.name as string,
      title: (route.meta?.title as string) || '未命名',
      icon: route.meta?.icon as string,
      closable: route.meta?.closable !== false, // 默认可关闭
      fullPath: route.fullPath,
      query: route.query,
    }

    tabs.value.push(newTab)
    activeTabPath.value = path
  }

  /**
   * 移除标签页
   */
  const removeTab = (path: string) => {
    const index = tabs.value.findIndex((tab) => tab.path === path)
    if (index === -1) return null

    const tab = tabs.value[index]
    tabs.value.splice(index, 1)

    // 如果删除的是当前激活的标签，需要激活相邻的标签
    if (activeTabPath.value === path) {
      if (tabs.value.length > 0) {
        // 优先激活右侧标签，没有则激活左侧
        const nextTab = tabs.value[index] || tabs.value[index - 1]
        return nextTab.fullPath
      }
    }

    return null
  }

  /**
   * 移除其他标签页
   */
  const removeOtherTabs = (path: string) => {
    tabs.value = tabs.value.filter((tab) => tab.path === path || !tab.closable)
    activeTabPath.value = path
  }

  /**
   * 移除左侧标签页
   */
  const removeLeftTabs = (path: string) => {
    const index = tabs.value.findIndex((tab) => tab.path === path)
    if (index === -1) return

    tabs.value = tabs.value.filter(
      (tab, i) => i >= index || !tab.closable
    )
  }

  /**
   * 移除右侧标签页
   */
  const removeRightTabs = (path: string) => {
    const index = tabs.value.findIndex((tab) => tab.path === path)
    if (index === -1) return

    tabs.value = tabs.value.filter(
      (tab, i) => i <= index || !tab.closable
    )
  }

  /**
   * 移除所有标签页（保留不可关闭的）
   */
  const removeAllTabs = () => {
    tabs.value = tabs.value.filter((tab) => !tab.closable)
    if (tabs.value.length > 0) {
      activeTabPath.value = tabs.value[0].path
      return tabs.value[0].fullPath
    }
    return '/'
  }

  /**
   * 刷新标签页（通过移除缓存实现）
   */
  const refreshTab = (path: string) => {
    const tab = tabs.value.find((t) => t.path === path)
    return tab?.name || null
  }

  /**
   * 更新标签标题
   */
  const updateTabTitle = (path: string, title: string) => {
    const tab = tabs.value.find((t) => t.path === path)
    if (tab) {
      tab.title = title
    }
  }

  /**
   * 设置当前激活的标签
   */
  const setActiveTab = (path: string) => {
    activeTabPath.value = path
  }

  /**
   * 获取缓存的组件名称列表（用于 KeepAlive）
   */
  const getCachedComponents = () => {
    return tabs.value
      .map((tab) => tab.name)
      .filter(Boolean)
  }

  // 持久化到 localStorage
  watch(
    tabs,
    (newTabs) => {
      localStorage.setItem('admin-tabs', JSON.stringify(newTabs))
    },
    { deep: true }
  )

  watch(activeTabPath, (newPath) => {
    localStorage.setItem('admin-active-tab', newPath)
  })

  /**
   * 从 localStorage 恢复标签
   */
  const restoreTabs = () => {
    try {
      const savedTabs = localStorage.getItem('admin-tabs')
      const savedActivePath = localStorage.getItem('admin-active-tab')

      if (savedTabs) {
        tabs.value = JSON.parse(savedTabs)
      }

      if (savedActivePath) {
        activeTabPath.value = savedActivePath
      }
    } catch (error) {
      console.error('恢复标签页失败:', error)
    }
  }

  /**
   * 清空所有标签（用于退出登录等场景）
   */
  const clearTabs = () => {
    tabs.value = []
    activeTabPath.value = ''
    localStorage.removeItem('admin-tabs')
    localStorage.removeItem('admin-active-tab')
  }

  return {
    // 状态
    tabs,
    activeTabPath,

    // 方法
    addTab,
    removeTab,
    removeOtherTabs,
    removeLeftTabs,
    removeRightTabs,
    removeAllTabs,
    refreshTab,
    updateTabTitle,
    setActiveTab,
    getCachedComponents,
    restoreTabs,
    clearTabs,
  }
})
